import type { AxiosRequestConfig } from 'axios'
import request from '@/shared/api/request'
import type { BatchRequestConfig } from '@/shared/api/use-api-batch'
import { sortLanguageOptionsByLabel } from '@/shared/utils/language'

// 语言类型枚举
export enum LanguageType {
  SOURCE = 'source',
  TARGET = 'target',
  BOTH = 'both'
}

// 语言基础接口
export interface Language {
  id: number
  chinese_name: string
  english_name: string
  language_code: string
  microsoft_code: string
  language_type: LanguageType
  status: boolean
  created_at: string
  updated_at: string
  statusLoading?: boolean
}

// 创建语言请求接口
export interface CreateLanguageRequest {
  chinese_name: string
  english_name: string
  language_code: string
  microsoft_code: string
  language_type: LanguageType
  status: boolean
}

// 更新语言请求接口
export interface UpdateLanguageRequest {
  id?: number
  chinese_name?: string
  english_name?: string
  language_code?: string
  microsoft_code?: string
  language_type?: LanguageType
  status?: boolean
}

// 语言列表响应接口
export interface LanguageListResponse {
  items: Language[]
  total: number
  page: number
  size: number
  pages: number
}

// 语言选项接口
export interface LanguageOption {
  value: string
  label: string
  microsoft_code: string
  language_type: LanguageType
}

// 语言对验证响应接口
export interface LanguagePairValidation {
  source_language: string
  target_language: string
  is_valid: boolean
}

// 微软代码响应接口
export interface MicrosoftCodeResponse {
  language_code: string
  microsoft_code: string
}

// 语言API类
export class LanguageAPI {
  /**
   * 获取语言列表
   */
  static async getLanguages(params?: {
    language_type?: LanguageType
    status?: boolean
    page?: number
    size?: number
  }): Promise<LanguageListResponse> {
    return request.get('/languages', { params })
  }

  /**
   * 获取语言选项（用于下拉框）
   */
  static async getLanguageOptions(
    params?: {
    language_type?: LanguageType
    status?: boolean
  },
    config?: AxiosRequestConfig | BatchRequestConfig,
  ): Promise<LanguageOption[]> {
    const apiParams = params ? {
      language_type: params.language_type,
      status_filter: params.status
    } : undefined
    const options = await request.get<LanguageOption[]>('/languages/options', {
      params: apiParams,
      ...config,
    })
    // 前端按中文名称拼音排序，避免后端数据库校对规则导致顺序混乱
    return sortLanguageOptionsByLabel(options)
  }

  /**
   * 获取单个语言配置
   */
  static async getLanguage(id: number): Promise<Language> {
    return request.get(`/languages/${id}`)
  }

  /**
   * 创建语言配置
   */
  static async createLanguage(data: CreateLanguageRequest): Promise<Language> {
    return request.post('/languages', data)
  }

  /**
   * 更新语言配置
   */
  static async updateLanguage(id: number, data: UpdateLanguageRequest): Promise<Language> {
    return request.put(`/languages/${id}`, data)
  }

  /**
   * 删除语言配置
   */
  static async deleteLanguage(id: number): Promise<{ message: string }> {
    return request.delete(`/languages/${id}`)
  }

  /**
   * 根据语言代码获取微软代码
   */
  static async getMicrosoftCode(languageCode: string): Promise<MicrosoftCodeResponse> {
    return request.get(`/languages/code/${languageCode}/microsoft-code`)
  }

  /**
   * 验证语言对是否有效
   */
  static async validateLanguagePair(
    sourceLanguage: string,
    targetLanguage: string
  ): Promise<LanguagePairValidation> {
    return request.post('/languages/validate-pair', null, {
      params: {
        source_language: sourceLanguage,
        target_language: targetLanguage
      }
    })
  }
}

// 导出API实例
export const languageApi = LanguageAPI

// 导出便捷函数
export const getLanguages = LanguageAPI.getLanguages
export const getLanguageOptions = LanguageAPI.getLanguageOptions
export const createLanguage = LanguageAPI.createLanguage
export const updateLanguage = LanguageAPI.updateLanguage
export const deleteLanguage = LanguageAPI.deleteLanguage

export default LanguageAPI